import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import Button from '@mui/material/Button';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import Settings from '@mui/icons-material/Settings';
import SpeakerNotesOutlinedIcon from '@mui/icons-material/SpeakerNotesOutlined';
import Delete from '@mui/icons-material/DeleteRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import SaveRounded from '@mui/icons-material/SaveRounded';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { Link, useHistory } from 'react-router-dom';
import LinkIcon from '@mui/icons-material/Link';
import DeleteDialog from '#component/shipment/overview/delete-dialog';
import ReportDialog from '#component/shipment/overview/report-dialog';
import StopDialog from '#component/shipment/overview/stop-dialog';
import ShipmentStatus from '#component/shipment/status';
import { getDeeplink } from '#action/shipment/deeplink';
import { ERROR, SUCCESS } from '#action/types';
import { fetchShipmentDeliveryCargo } from '#action/shipment/shipment-delivery-cargo';
import { fetchShipmentNotificationRecipients } from '#action/shipment/recipients';
import { setCopiedShipment, setCopiedShipmentStatus } from '#action/shipment/copied-shipment';
import { isEnabledModuleById } from '#component/project/form/helper';
import propTypes from './prop-types';
import {
  Icon,
  DisabledIcon,
  IconSecond,
  StyledMenuItem,
  DisabledText,
  Preloader,
  AlertPreloader,
  AlertStatusCnt
} from './styles';
import { writeToClipboard, requestStatuses } from '#helper';

const Actions = props => {
  const {
    account,
    children,
    dispatch,
    shipment: {
      id: shipmentId,
      defaultDevices = [],
      shipmentStatus,
      project = {}
    },
    alertStatusComponent,
    tooltip,
    isMetricsLoaded
  } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const [showDeleteDialog, setDeleteDialog] = useState(false);
  const [showStopDialog, setStopDialog] = useState(false);
  const [showReportDialog, setReportDialog] = useState(false);
  const history = useHistory();

  const shipment = useSelector(s => s.shipment, shallowEqual);
  const copiedShipment = useSelector(s => s.copiedShipment, shallowEqual);

  const trailModuleEnabled = isEnabledModuleById(project.modules, 3);

  const isAnyMeasurement = defaultDevices.some(d =>
    Object.values(d.metrics ?? {})
      .some(v => v.length));

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeeplinkClick = () =>
    writeToClipboard(
      getDeeplink(shipmentId, account.measurementSystem)
    )
      .then(() => {
        handleClose();

        dispatch({
          type: SUCCESS,
          payload: {
            message: 'Link copied to clipboard'
          }
        });
      })
      .catch(error => {
        dispatch({
          type: ERROR,
          payload: {
            message: `Copying link to clipboard failed. ${error}`
          }
        });
      });

  useEffect(() => {
    if (shipment.requestStatus === 'succeeded' && copiedShipment.init) {
      dispatch(setCopiedShipment(shipment));
      if (copiedShipment.status === 'succeeded') {
        history.push('/addshipment?copied');
      }
    }
  }, [shipment, copiedShipment, history, dispatch]);

  const handleCopyShipment = async () => {
    dispatch(setCopiedShipmentStatus({ status: requestStatuses.loading }));

    await Promise.all([
      dispatch(fetchShipmentNotificationRecipients(shipmentId)),
      dispatch(fetchShipmentDeliveryCargo(shipmentId, true))
    ]);

    dispatch(setCopiedShipmentStatus({ status: requestStatuses.succeeded, init: true }));
  };

  return (
    <>
      {account && account.userType <= 20 ?
        <Tooltip title={tooltip}>
          <Button onClick={handleClick}>
            <ShipmentStatus status={shipmentStatus} />
            <AlertStatusCnt aria-label="Shipment status">
              {alertStatusComponent ||
                <AlertPreloader size={20} />}
            </AlertStatusCnt>
            { React.cloneElement(children, { style: { cursor: 'pointer' } }) }
            <ArrowDropDownIcon />
          </Button>
        </Tooltip> :
        children }
      {account && account.userType <= 20 &&
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <StyledMenuItem
          component={Link}
          to={`/shipment/edit/${shipmentId}`}
        >
          <Icon><Settings /></Icon>
          <span>Settings</span>
        </StyledMenuItem>
        {trailModuleEnabled &&
          <StyledMenuItem
            component={Link}
            to={`/shipment-log?shipment=${shipmentId}`}
          >
            <Icon><SpeakerNotesOutlinedIcon /></Icon>
            <span>Audit trail</span>
          </StyledMenuItem>}
        {isAnyMeasurement ?
          <StyledMenuItem
            onClick={() => {
              setAnchorEl(null);
              setReportDialog(true);
            }}
          >
            <Icon><SaveRounded /></Icon>
            <span>Report</span>
          </StyledMenuItem> : isMetricsLoaded ?
            <StyledMenuItem>
              <DisabledIcon><SaveRounded /></DisabledIcon>
              <DisabledText>Report</DisabledText>
            </StyledMenuItem> :
            <StyledMenuItem>
              <Preloader size={20} />
              <DisabledText>Report</DisabledText>
            </StyledMenuItem>}
        <StyledMenuItem onClick={handleDeeplinkClick}>
          <Icon><LinkIcon /></Icon>
          <span>Copy public link</span>
        </StyledMenuItem>
        <StyledMenuItem onClick={handleCopyShipment}>
          <Icon><FileCopyIcon /></Icon>
          <span>Copy shipment</span>
        </StyledMenuItem>
        {!shipment.done &&
          <StyledMenuItem
            onClick={() => {
              setAnchorEl(null);
              setStopDialog(true);
            }}
          >
            <IconSecond><StopRoundedIcon /></IconSecond>
            <span>Stop</span>
          </StyledMenuItem>}
        <StyledMenuItem
          onClick={() => {
            setAnchorEl(null);
            setDeleteDialog(true);
          }}
        >
          <IconSecond><Delete /></IconSecond>
          <span>Delete</span>
        </StyledMenuItem>
      </Menu>}
      <DeleteDialog
        shipment={shipment}
        showDialog={showDeleteDialog}
        setShowDialog={setDeleteDialog}
        dispatch={dispatch}
        history={history}
      />
      <StopDialog
        shipment={shipment}
        dispatch={dispatch}
        showDialog={showStopDialog}
        setShowDialog={setStopDialog}
      />
      <ReportDialog
        showDialog={showReportDialog}
        setReportDialog={setReportDialog}
        {...props}
      />
    </>
  );
};

export default Actions;

Actions.propTypes = propTypes;
